import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const formEndpoint = "https://readdy.ai/api/form/db9jgork13ra8mig94vg";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "编辑视角",
    desc: "我们不做无差别的资源堆叠。每一部进入推荐位的作品，都由编辑看过并写下理由。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "结构化资料",
    desc: "类型、地区、年份、评分与演职人员被整理成统一结构，方便你横向比较而不是凭感觉猜。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "克制的表达",
    desc: "影评与访谈尽量少用形容词。我们更愿意把作品放回它的语境里，让读者自己做判断。",
  },
];

export default function About() {
  useDocumentMeta(
    "关于我们 - 午夜影院",
    "了解午夜影院的定位、内容准则与联系方式。我们是一个专注电影与电视剧的内容门户，用清晰分类与深度专题帮助用户发现优质影视内容。"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("company_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setSuccessMsg("反馈已收到，感谢你的留言。");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value.trim());
      }
    });

    try {
      const response = await fetch(formEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const failed =
        !response.ok ||
        parsed?.code !== "OK" ||
        /spam/i.test(String(serverMsg)) ||
        /spam/i.test(responseText);

      if (failed) {
        setStatus("error");
        setFormError(
          serverMsg.replace(/form data is spam/gi, "内容未通过校验") ||
            "提交没有成功，请稍后再试，或直接发送邮件至 hello@midnightcinema.cn。"
        );
        return;
      }

      setStatus("success");
      setSuccessMsg("反馈已提交，我们会在两个工作日内查看，感谢你的留言。");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络连接异常，提交没有完成。请检查网络后重试，或直接发送邮件给我们。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-accent-400";

  return (
    <div className="w-full bg-background-50">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <span className="pointer-events-none absolute -right-6 top-1/2 hidden -translate-y-1/2 font-heading text-[130px] font-bold uppercase text-background-50/5 md:block">
          About
        </span>
        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-9 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-background-400">
            <Link to="/" className="transition-colors duration-300 hover:text-secondary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-200">关于我们</span>
          </nav>
          <p className="mt-6 flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-secondary-400">
            <span className="h-[2px] w-6 bg-accent-500" />
            About Midnight Cinema
          </p>
          <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-background-50 md:text-[40px]">
            关于午夜影院
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-background-200/90">
            午夜影院是一个综合影视内容门户，聚合电影与电视剧资料，并配套影评、访谈与主题专题。我们希望把“选片”这件事变得更简单，也把阅读作品的过程变得更有耐心。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
          <div>
            <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground-950 md:text-2xl">
              我们的定位
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-600">
              <p>
                午夜影院服务于各年龄层的影视爱好者：想快速找到一部片子的观众、习惯追更的剧迷，以及关注行业动态与深度评论的读者。网站以清晰的分类与多维筛选为骨架，以详实的资料与编辑内容为血肉。
              </p>
              <p>
                我们不做播放与下载，也不要求你注册登录。网站的目标只有一个：在你打开页面的几分钟内，帮你判断“这部作品是否值得看”，或者“这条资讯是否值得读”。
              </p>
              <p>
                目前站内内容为编辑整理的示例数据，用于展示完整的浏览、筛选与阅读体验。后续接入内容接口后，资料与资讯将持续更新。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-900 px-4 py-2.5 text-sm text-background-50 transition-colors duration-300 hover:bg-primary-800"
              >
                浏览电影分类
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/special"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors duration-300 hover:border-accent-400 hover:text-accent-700"
              >
                查看专题策划
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "24", label: "部影视资料", tone: "primary" },
              { value: "06", label: "篇影视资讯", tone: "plain" },
              { value: "04", label: "期编辑专题", tone: "plain" },
              { value: "22", label: "个类型标签", tone: "accent" },
            ].map((stat) => (
              <div
                key={stat.label}
                className={`rounded-lg border p-5 ${
                  stat.tone === "primary"
                    ? "border-transparent bg-primary-900"
                    : stat.tone === "accent"
                      ? "border-secondary-200 bg-secondary-50"
                      : "border-background-200 bg-background-100"
                }`}
              >
                <p
                  className={`font-heading text-2xl font-semibold md:text-3xl ${
                    stat.tone === "primary" ? "text-secondary-300" : "text-foreground-950"
                  }`}
                >
                  {stat.value}
                </p>
                <p
                  className={`mt-2 text-xs ${
                    stat.tone === "primary" ? "text-background-300" : "text-foreground-500"
                  }`}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground-950 md:text-2xl">
            内容准则
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            三条简单的原则，决定了我们选择什么、怎么写、以及不写什么。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {values.map((value) => (
              <div key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                  <i className={`${value.icon} text-lg`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {value.title}
                </h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <div>
            <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground-950 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              内容纠错、合作咨询、选题建议，都欢迎通过下面任意方式找到我们。工作日的留言通常会在两个工作日内回复。
            </p>

            <dl className="mt-6 space-y-4">
              {[
                { icon: "ri-mail-line", label: "邮箱", value: "hello@midnightcinema.cn" },
                { icon: "ri-time-line", label: "在线时间", value: "每日 10:00 - 24:00" },
                { icon: "ri-map-pin-line", label: "编辑部", value: "线上内容编辑部" },
                { icon: "ri-question-answer-line", label: "合作方向", value: "内容共创 / 专题协作 / 资料授权" },
              ].map((row) => (
                <div key={row.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-accent-700">
                    <i className={row.icon} />
                  </span>
                  <div>
                    <dt className="text-[11px] text-foreground-400">{row.label}</dt>
                    <dd className="mt-0.5 text-sm text-foreground-900">{row.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-6 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium text-secondary-900">
                <i className="ri-information-line" />
                关于表单
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-secondary-800/85">
                下方表单为真实收集表单，提交后由站内后台记录。我们不会将你的联系方式用于其他用途。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
            <h3 className="font-heading text-base font-semibold text-foreground-950">给我们留言</h3>
            <p className="mt-1 text-xs text-foreground-500">带 * 的为必填项。</p>

            <form
              id="about-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 space-y-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs text-foreground-600">
                    称呼 *
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="怎么称呼你"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs text-foreground-600">
                    邮箱 *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-topic" className="mb-1.5 block text-xs text-foreground-600">
                  反馈类型
                </label>
                <select id="contact-topic" name="topic" className={inputClass} defaultValue="内容纠错">
                  <option value="内容纠错">内容纠错</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="选题建议">选题建议</option>
                  <option value="使用问题">使用问题</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-xs text-foreground-600">
                  留言内容 *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请简单描述你的问题或建议（500 字以内）"
                  className={`${inputClass} resize-none`}
                />
              </div>

              <div className="contact-form-guest-field" aria-hidden="true">
                <label htmlFor="contact-company-alt">公司名称</label>
                <input
                  id="contact-company-alt"
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "success" ? (
                <p
                  role="status"
                  className="flex items-start gap-1.5 rounded-md bg-secondary-100 px-3 py-2.5 text-xs leading-relaxed text-secondary-900"
                >
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  {successMsg}
                </p>
              ) : null}

              {status === "error" && formError ? (
                <p
                  role="alert"
                  className="flex items-start gap-1.5 rounded-md bg-accent-100 px-3 py-2.5 text-xs leading-relaxed text-accent-900"
                >
                  <i className="ri-error-warning-line mt-0.5" />
                  {formError}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full cursor-pointer whitespace-nowrap rounded-md bg-accent-500 py-3 text-sm font-medium text-primary-950 transition-colors duration-300 hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100 py-12 md:py-14">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">版权与免责声明</h2>
          <div className="mt-4 space-y-3 text-xs leading-7 text-foreground-600 md:text-[13px]">
            <p>
              本站所有影视作品的文字资料、评分与演职人员信息均为编辑整理的示例内容，仅用于展示网站功能与阅读体验，不代表真实作品信息。
            </p>
            <p>
              本站不提供任何影片的在线播放、下载或资源分发服务，页面中不包含播放器与外链资源。若本站内容涉及第三方权益，请通过上方联系方式与我们沟通，我们会及时处理。
            </p>
            <p>
              收藏与浏览记录保存在你的浏览器本地，不会上传至服务器。清除浏览器数据后，这些记录将同时被清除。
            </p>
            <p>© 2025 午夜影院 Midnight Cinema. 保留所有权利。</p>
          </div>
        </div>
      </section>
    </div>
  );
}